import { useState } from "react";
import { api } from "@/lib/api";
import analytics from "@/lib/analytics";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, XCircle, Gift, Lock, ArrowRight } from "lucide-react";

/**
 * Pre-checkout confirmation dialog.
 * - Optional promo code (validated live).
 * - Shows an updated order summary with trial period + billing-start date.
 * - Then redirects to Stripe hosted Checkout (which collects the card upfront).
 */
export default function CheckoutConfirmDialog({ open, onOpenChange, plan }) {
  const [code, setCode] = useState("");
  const [validating, setValidating] = useState(false);
  const [applied, setApplied] = useState(null); // {code, trial_days, billing_starts, message}
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCode("");
    setApplied(null);
    setError(null);
  };

  const closeAndReset = (v) => {
    if (!submitting) {
      onOpenChange(v);
      if (!v) reset();
    }
  };

  const applyPromo = async () => {
    setError(null);
    setApplied(null);
    if (!code.trim()) return;
    setValidating(true);
    try {
      const { data } = await api.post("/payments/validate-promo", { code: code.trim() });
      if (data.valid) {
        setApplied(data);
      } else {
        setError(data.error || "That code isn't recognised.");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't validate that code. Please try again.");
    } finally {
      setValidating(false);
    }
  };

  const confirm = async () => {
    if (!plan) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key,
        origin_url: window.location.origin,
        promo_code: applied?.code || null,
      });
      analytics.track("checkout_confirmed", {
        plan_id: plan.id,
        plan_name: plan.name,
        promo_code: applied?.code || null,
        trial_days: data.trial_days,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || "We couldn't start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  if (!plan) return null;

  return (
    <Dialog open={open} onOpenChange={closeAndReset}>
      <DialogContent
        className="max-w-md p-0 gap-0 overflow-hidden"
        data-testid="checkout-confirm-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3">
          <div className="vq-overline mb-1">Confirm your plan</div>
          <DialogTitle className="font-display font-bold text-2xl text-slate-900">
            {plan.name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Review your plan and apply an optional promo code before continuing to Stripe.
          </DialogDescription>
        </DialogHeader>

        {/* Order summary */}
        <div className="px-6 pb-4 border-b border-slate-100">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-sm text-slate-600">Plan price</span>
            <span
              className="font-display font-bold text-xl text-slate-900"
              data-testid="checkout-summary-price"
            >
              {plan.price_display}
            </span>
          </div>
          {applied ? (
            <>
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm text-slate-600">Promo</span>
                <span
                  className="font-mono text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                  data-testid="checkout-summary-promo"
                >
                  {applied.code} · {applied.trial_days}-day trial
                </span>
              </div>
              <div className="flex items-baseline justify-between pt-2 border-t border-dashed border-slate-200 mt-2">
                <span className="text-sm text-slate-900 font-semibold">Charge today</span>
                <span
                  className="font-display font-bold text-2xl text-emerald-600"
                  data-testid="checkout-summary-total"
                >
                  $0.00
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-2" data-testid="checkout-summary-billing-starts">
                Billing starts <b className="text-slate-700">{applied.billing_starts}</b>. Cancel anytime before then.
              </div>
            </>
          ) : (
            <div className="text-xs text-slate-500 mt-1">
              Billed today. Cancel anytime from your account settings.
            </div>
          )}
        </div>

        {/* Promo code field */}
        <div className="px-6 py-4 border-b border-slate-100">
          <Label htmlFor="promo-code" className="text-xs font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <Gift className="w-3 h-3" /> Promo code <span className="normal-case tracking-normal text-slate-400">(optional)</span>
          </Label>
          {!applied ? (
            <div className="mt-1.5 flex gap-2">
              <Input
                id="promo-code"
                data-testid="checkout-promo-input"
                placeholder="e.g. TRIAL30"
                value={code}
                onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }}
                maxLength={40}
                disabled={validating || submitting}
                className="flex-1"
              />
              <Button
                type="button"
                onClick={applyPromo}
                disabled={!code.trim() || validating || submitting}
                variant="outline"
                data-testid="checkout-promo-apply"
                className="h-10"
              >
                {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
              </Button>
            </div>
          ) : (
            <div
              className="mt-1.5 p-3 rounded-md bg-emerald-50 border border-emerald-200 flex items-start gap-2"
              data-testid="checkout-promo-success"
            >
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-emerald-900 font-medium">
                  {applied.message}
                </div>
                {applied.description && (
                  <div className="text-xs text-emerald-700 mt-0.5">{applied.description}</div>
                )}
              </div>
              <button
                type="button"
                onClick={reset}
                data-testid="checkout-promo-remove"
                className="text-xs text-slate-500 hover:text-slate-900 font-mono uppercase tracking-wider"
              >
                Remove
              </button>
            </div>
          )}
          {error && (
            <div
              className="mt-2 p-2.5 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2"
              data-testid="checkout-promo-error"
            >
              <XCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">{error}</div>
            </div>
          )}
        </div>

        {/* Confirm */}
        <div className="px-6 py-4">
          <Button
            onClick={confirm}
            disabled={submitting}
            data-testid="checkout-confirm-btn"
            className="w-full h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to Stripe…</>
            ) : (
              <>
                {applied ? "Start 30-day free trial" : "Continue to payment"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
          <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500 justify-center">
            <Lock className="w-3 h-3" />
            <span>
              Card collected securely by Stripe. {applied ? "You won't be charged until " + applied.billing_starts + "." : "PCI-DSS compliant · No card data stored on our servers."}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
